import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  WProjectCustomizationPageView,
  WProjectCustomizationElementView,
} from '@zola/svc-web-api-ts-client';
import { TrashIcon } from '@zola/zola-ui/src/components/SvgIcons';
import { AppDispatch } from 'reducers/index';
import { getQRCodeState } from 'cards/selectors/qrCode';
import { getPageEntities, getElementEntities } from 'cards/selectors/cardProjectSelector';
import { updateQRCodeState, toggleQRCode } from 'cards/actions/qrCode';
import { Box, ButtonBox } from './styles';

export interface QRDeleteButtonProps {
  editable?: boolean;
}

const QRDeleteButton = ({ editable }: QRDeleteButtonProps): JSX.Element | null => {
  const dispatch = useDispatch<AppDispatch>();
  const qrCodeState = useSelector(getQRCodeState);
  const pages: Record<
    string,
    (WProjectCustomizationPageView & { elements: string[] }) | undefined
  > = useSelector(getPageEntities);
  const elements: Record<string, WProjectCustomizationElementView | undefined> = useSelector(
    getElementEntities
  );

  const qrPage = useMemo(
    () =>
      qrCodeState?.qrElement?.uuid && pages && elements
        ? Object.values(pages).find(page =>
            page?.elements?.some(
              (pageElementUUID: string) =>
                Object.values(elements).find(el => pageElementUUID === el?.uuid)?.uuid ===
                qrCodeState?.qrElement?.uuid
            )
          )
        : null,
    [qrCodeState, pages, elements]
  );

  const onDelete = useCallback(() => {
    if (!qrPage || !qrCodeState?.enabled || qrCodeState?.loading || !qrCodeState?.projectSynced) {
      return;
    }

    dispatch(toggleQRCode(false, qrPage))
      .then(() => {
        dispatch(updateQRCodeState({ loading: false }));
      })
      .catch(e => {
        throw e;
      });
  }, [dispatch, qrPage, qrCodeState, elements]);

  if (
    editable === false ||
    !qrCodeState?.enabled ||
    !qrCodeState?.qrElement ||
    !qrCodeState?.projectSynced
  ) {
    return null;
  }

  return (
    <Box>
      <ButtonBox onClick={onDelete}>
        <TrashIcon /> Delete
      </ButtonBox>
    </Box>
  );
};

export default QRDeleteButton;
